import { Swords, Flame } from 'lucide-react';

/**
 * Phase 2K PR 1 — Rivalry Badge component.
 *
 * Compact, narrative-first rivalry surface. Three render modes:
 *
 *   <ArenaxRivalryBadge hint="HSR Titans haven't forgotten last season's semifinal 👀" />
 *   → tribal narrative line, mounted on tournament fixtures + game cards.
 *
 *   <ArenaxRivalryBadge chip="HSR Titans lead 3-2" streakChip="W3 · Strikers" />
 *   → compact dual-chip mode for squad cards / list views.
 *
 *   <ArenaxRivalryBadge hint="…" chip="…" streakChip="…" />
 *   → combined: narrative line + chips beneath.
 *
 * Tone: NEVER analytics — only commentary copy passed in via props.
 * See narrative_style_guide.md.
 */
export default function ArenaxRivalryBadge({ hint, chip, streakChip, variant = 'inline', testId = 'arenax-rivalry-badge' }) {
  if (!hint && !chip && !streakChip) return null;

  if (variant === 'compact') {
    return (
      <div className="inline-flex flex-wrap items-center gap-1.5" data-testid={testId}>
        {chip && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide text-rose-200 bg-rose-500/10 border border-rose-500/30">
            <Swords className="h-2.5 w-2.5" />
            {chip}
          </span>
        )}
        {streakChip && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider text-amber-200 bg-amber-500/10 border border-amber-500/30">
            <Flame className="h-2.5 w-2.5" />
            {streakChip}
          </span>
        )}
      </div>
    );
  }

  // inline (default) — narrative-led
  return (
    <div className="mt-2 flex items-start gap-1.5" data-testid={testId}>
      <Swords className="h-3 w-3 text-rose-300 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        {hint && (
          <p className="text-[11px] text-rose-100 italic leading-snug" data-testid={`${testId}-hint`}>
            {hint}
          </p>
        )}
        {(chip || streakChip) && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {chip && (
              <span className="text-[9px] uppercase tracking-wider font-bold text-rose-200/80"
                data-testid={`${testId}-chip`}>
                {chip}
              </span>
            )}
            {streakChip && (
              <span className="text-[9px] uppercase tracking-wider font-black text-amber-200"
                data-testid={`${testId}-streak`}>
                {streakChip}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
